import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { colors, fonts } from '../brand';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

export default function TermsOfService() {

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Terms of Service — Elephants Answering</title>
        <meta name="description" content="The terms that govern your use of Elephants Answering, including membership, billing, cancellation, and community guidelines." />
      </Helmet>
      <SiteNav />

      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <h1 style={styles.heroTitle}>Terms of Service</h1>
          <p style={styles.heroSub}>Last updated: September 18, 2026</p>
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.inner}>

          <p style={styles.body}>
            These Terms of Service ("Terms") govern your use of the website at <strong>elephantsanswering.com</strong> and any related services (collectively, the "Service"), operated by Elephants Answering, PBC ("we," "us," or "our"), a Colorado public benefit corporation.
          </p>
          <p style={styles.body}>
            By creating an account, subscribing, or otherwise using the Service, you agree to these Terms. If you do not agree, please do not use the Service.
          </p>

          <h2 style={styles.heading}>1. What Elephants Answering Is — and Is Not</h2>
          <p style={styles.body}>
            Elephants Answering is a community and information resource for women navigating divorce. We share lived experience, plain-language explanations, and links to outside resources.
          </p>
          <p style={styles.body}>
            <strong>We are not a law firm, and nothing on the Service is legal advice.</strong> We are not attorneys, therapists, financial advisors, or tax professionals, and no content on the Service creates a professional relationship of any kind. Every divorce is different. Please consult a licensed attorney in your state before making decisions about your own situation.
          </p>
          <p style={styles.body}>
            Information on the Service is general and educational. Laws vary by state and change over time. We do not guarantee that any content is current, complete, or applicable to your circumstances.
          </p>
          <p style={styles.body}>
            <strong>If you are in immediate danger, call 911.</strong> The National Domestic Violence Hotline is available at 1.800.799.SAFE (7233), or text START to 88788.
          </p>

          <h2 style={styles.heading}>2. Eligibility and Your Account</h2>
          <p style={styles.body}>
            You must be at least 18 years old to use the Service. You are responsible for the accuracy of the information you provide, for keeping your password confidential, and for all activity that occurs under your account. Please tell us promptly if you believe your account has been accessed without your permission.
          </p>
          <p style={styles.body}>
            You may not share your account credentials with others or allow anyone else to access member-only content through your account.
          </p>

          <h2 style={styles.heading}>3. Membership and Billing</h2>
          <p style={styles.body}>
            Some parts of the Service require a paid membership. Current pricing is shown at the point of purchase.
          </p>
          <p style={styles.body}>
            <strong>Recurring billing.</strong> Paid memberships are subscriptions that renew automatically at the interval shown at checkout, until cancelled. By subscribing, you authorize us to charge your payment method on a recurring basis.
          </p>
          <p style={styles.body}>
            <strong>Payment processing.</strong> Payments are processed by Stripe. We do not receive or store your full card details. Your use of Stripe is subject to Stripe's own terms and privacy policy.
          </p>
          <p style={styles.body}>
            <strong>Price changes.</strong> We may change membership pricing. If we do, we will give you notice before the change takes effect, and it will not apply to the billing period you have already paid for. Continuing your membership after a price change takes effect means you accept the new price.
          </p>
          <p style={styles.body}>
            <strong>Failed payments.</strong> If a payment fails, we may retry it. If it continues to fail, your access to member-only content may be suspended or ended.
          </p>

          <h2 style={styles.heading}>4. Cancellation and Refunds</h2>
          <p style={styles.body}>
            <strong>You can cancel at any time</strong> through the <a href="https://billing.stripe.com/p/login/7sYbJ13FE1Ny2yQ6w9cMM00" target="_blank" rel="noopener noreferrer" style={styles.link}>subscription management portal</a>, or by emailing us at <a href="mailto:info@elephantsanswering.com" style={styles.link}>info@elephantsanswering.com</a>.
          </p>
          <p style={styles.body}>
            When you cancel, your membership remains active through the end of the billing period you have already paid for, and it will not renew after that. We do not provide partial refunds for unused time in a billing period.
          </p>
          <p style={styles.body}>
            If you believe you were charged in error, contact us at <a href="mailto:info@elephantsanswering.com" style={styles.link}>info@elephantsanswering.com</a> and we will work with you in good faith to resolve it.
          </p>

          <h2 style={styles.heading}>5. What You Share With Us</h2>
          <p style={styles.body}>
            The Service lets you submit content — voice memos, questions, feedback, and messages ("Your Content"). You keep ownership of Your Content.
          </p>
          <p style={styles.body}>
            By submitting Your Content, you give us a non-exclusive, worldwide, royalty-free licence to store, reproduce, edit for length or clarity, and publish it as part of the Service. We may publish it anonymously or under a first name only. <strong>If you would prefer your submission not be published, or you want it removed after publication, email us and we will honour that.</strong>
          </p>
          <p style={styles.body}>
            Please do not submit content that identifies other people without their consent, that you are legally prohibited from sharing, or that is subject to a court order, protective order, settlement agreement, or non-disclosure obligation. You are responsible for making sure you have the right to share what you share.
          </p>

          <h2 style={styles.heading}>6. Community Guidelines</h2>
          <p style={styles.body}>This is a support community. When using the Service, please do not:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Harass, threaten, or abuse anyone</li>
            <li style={styles.listItem}>Share another member's story, identity, or information outside the community</li>
            <li style={styles.listItem}>Post content that is unlawful, defamatory, or deliberately false</li>
            <li style={styles.listItem}>Use the Service to advertise or solicit, without our written permission</li>
            <li style={styles.listItem}>Attempt to access accounts, data, or systems you are not authorised to access</li>
            <li style={styles.listItem}>Scrape, copy, or republish member content or paid content elsewhere</li>
          </ul>
          <p style={styles.body}>
            We may suspend or end an account that breaks these guidelines. Where a member's safety is involved, we may act immediately and without notice.
          </p>

          <h2 style={styles.heading}>7. Our Content</h2>
          <p style={styles.body}>
            Content we create — writing, roadmaps, glossaries, design, and the Elephants Answering name and logo — belongs to us and is protected by copyright and trademark law. You may use it for your own personal, non-commercial purposes. You may not republish, resell, or redistribute it without our written permission.
          </p>

          <h2 style={styles.heading}>8. Third-Party Links and Resources</h2>
          <p style={styles.body}>
            The Service links to outside organisations, legal aid providers, and other resources. We provide these as a convenience. We do not control them, do not endorse everything they say or do, and are not responsible for their content, services, or practices.
          </p>

          <h2 style={styles.heading}>9. Availability and Changes to the Service</h2>
          <p style={styles.body}>
            We are a small and growing organisation. We may add, change, or discontinue parts of the Service. We do not guarantee uninterrupted or error-free access. If we discontinue a paid feature you are actively paying for, we will give you reasonable notice.
          </p>

          <h2 style={styles.heading}>10. Disclaimer of Warranties</h2>
          <p style={styles.body}>
            The Service is provided "as is" and "as available," without warranties of any kind, whether express or implied, including any implied warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the Service will meet your needs or that information on it is accurate or complete.
          </p>

          <h2 style={styles.heading}>11. Limitation of Liability</h2>
          <p style={styles.body}>
            To the fullest extent permitted by law, Elephants Answering, PBC and its officers, directors, employees, and contributors will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of data, income, or profits, arising from your use of the Service.
          </p>
          <p style={styles.body}>
            To the fullest extent permitted by law, our total liability for any claim relating to the Service will not exceed the greater of (a) the amount you paid us in the twelve months before the claim arose, or (b) one hundred US dollars.
          </p>
          <p style={styles.body}>
            Some jurisdictions do not allow certain limitations of liability, so parts of this section may not apply to you.
          </p>

          <h2 style={styles.heading}>12. Indemnity</h2>
          <p style={styles.body}>
            You agree to indemnify and hold harmless Elephants Answering, PBC from claims, damages, and reasonable legal costs arising out of content you submit, your use of the Service, or your breach of these Terms.
          </p>

          <h2 style={styles.heading}>13. Ending Your Access</h2>
          <p style={styles.body}>
            You may stop using the Service and close your account at any time. We may suspend or end your access if you breach these Terms, if required by law, or if continuing would put other members at risk. If we end a paid membership without cause, we will refund the unused portion of your current billing period.
          </p>

          <h2 style={styles.heading}>14. Privacy</h2>
          <p style={styles.body}>
            Our <Link to="/privacy" style={styles.link}>Privacy Policy</Link> explains how we collect, use, and protect your information, and forms part of these Terms.
          </p>

          <h2 style={styles.heading}>15. Changes to These Terms</h2>
          <p style={styles.body}>
            We may update these Terms. When we do, we will change the "Last updated" date above. If a change materially affects your rights as a paying member, we will give you notice by email before it takes effect. Continuing to use the Service after that means you accept the updated Terms.
          </p>

          <h2 style={styles.heading}>16. Governing Law</h2>
          <p style={styles.body}>
            These Terms are governed by the laws of the State of Colorado, without regard to its conflict of law rules. Any dispute will be brought in the state or federal courts located in Denver, Colorado, and you and we consent to the jurisdiction of those courts. Nothing here prevents either of us from seeking relief in small claims court.
          </p>

          <h2 style={styles.heading}>17. General</h2>
          <p style={styles.body}>
            If any part of these Terms is found unenforceable, the rest remains in effect. Our not enforcing a provision is not a waiver of it. These Terms, together with the Privacy Policy, are the entire agreement between you and us regarding the Service.
          </p>

          <h2 style={styles.heading}>18. Contact</h2>
          <p style={styles.body}>
            Questions about these Terms? Email{' '}
            <a href="mailto:info@elephantsanswering.com" style={styles.contactLink}>info@elephantsanswering.com</a>.
          </p>
          <p style={styles.body}>
            Elephants Answering, PBC<br />
            Denver, Colorado
          </p>

        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: colors.cream, fontFamily: fonts.body },

  hero: { backgroundColor: colors.navy, padding: '64px 40px 48px' },
  heroInner: { maxWidth: '760px', margin: '0 auto' },
  heroTitle: { fontFamily: fonts.heading, color: colors.white, fontSize: '32px', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '10px' },
  heroSub: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.5)', fontSize: '13px', letterSpacing: '0.3px' },

  section: { padding: '64px 40px' },
  inner: { maxWidth: '760px', margin: '0 auto' },

  heading: { fontFamily: fonts.heading, color: colors.navy, fontSize: '17px', fontWeight: '600', letterSpacing: '0.3px', marginTop: '40px', marginBottom: '12px' },
  body: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.9', marginBottom: '16px' },
  list: { paddingLeft: '20px', marginBottom: '16px' },
  listItem: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '2', marginBottom: '4px' },
  link: { color: colors.rose, textDecoration: 'underline' },
  contactLink: { color: colors.rose, fontFamily: fonts.body, fontSize: '14px', textDecoration: 'underline' },
};
